
import { format } from 'date-fns'
import Vue from 'vue'
import {
  FieldErrors,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PasswordFields,
  Profile,
  ProfileRefused,
  changePassword,
  loadProfile,
  passwordFormAfter,
  saveProfile
} from '../profile'

// The rendering, and the decisions that are about rendering rather than about
// requests: which fields exist, what is shown but not editable, where each error
// appears, and what the two forms hold once an attempt has finished.
//
// Everything that decides what goes on the wire is in `../profile` -- what a save
// carries, what a refusal means, which length bounds apply, and what to do about
// a password change revoking the session it was made with -- because a component
// is never mounted by the tests and a decision made inside one cannot be
// observed. What is left here is the half that could not be tested anyway.
export default Vue.extend({
  name: 'Profile',

  data: () => ({
    profile: null as Profile | null,
    // The three fields of the profile form. `username` is not bound to anything a
    // person can type into; it is the value the screen is showing, kept so that a
    // save can state it -- which is what gives the server the chance to refuse a
    // change of it rather than being told nothing about it.
    name: '',
    email: '',
    username: '',
    errors: {} as FieldErrors,
    saveMessage: '',
    saved: false,
    saving: false,
    currentPassword: '',
    newPassword: '',
    passwordErrors: {} as FieldErrors,
    passwordMessage: '',
    passwordChanged: false,
    changing: false,
    loadMessage: '',
    minLength: PASSWORD_MIN_LENGTH,
    maxLength: PASSWORD_MAX_LENGTH
  }),

  created () {
    this.load()
  },

  methods: {
    // Rendered rather than rendered-and-editable, and read from the record the
    // server sent rather than from the store: the store holds the session, and the
    // session is not the account.
    async load (): Promise<void> {
      this.loadMessage = ''

      try {
        this.profile = await loadProfile()
        this.username = this.profile.username
        this.name = this.profile.name
        this.email = this.profile.email
      } catch (error) {
        this.profile = null
        this.loadMessage = error instanceof ProfileRefused
          ? error.message
          : 'Your profile could not be loaded. Please try again.'
      }
    },

    async save (): Promise<void> {
      this.saving = true
      this.saved = false
      this.errors = {}
      this.saveMessage = ''

      try {
        // The server's answer rather than what was typed, so a value it normalised
        // on the way in is what the form goes on showing.
        const saved = await saveProfile({
          name: this.name,
          email: this.email,
          username: this.username
        })

        this.profile = saved
        this.username = saved.username
        this.name = saved.name
        this.email = saved.email
        this.saved = true
      } catch (error) {
        this.saved = false
        this.reportRefusal(error, (fields, message) => {
          this.errors = fields
          this.saveMessage = message
        })
      } finally {
        this.saving = false
      }
    },

    async change (): Promise<void> {
      this.changing = true
      this.passwordChanged = false
      this.passwordErrors = {}
      this.passwordMessage = ''

      // Taken first, because what the form is left holding is decided from what
      // was in it and not from what it holds afterwards.
      const attempted: PasswordFields = {
        currentPassword: this.currentPassword,
        newPassword: this.newPassword
      }

      try {
        await changePassword(attempted.currentPassword, attempted.newPassword)

        this.adopt(passwordFormAfter(attempted, true))
        this.passwordChanged = true
      } catch (error) {
        // Asked before the refusal is reported, because the answer does not depend
        // on what the refusal said: the current password is gone either way.
        this.adopt(passwordFormAfter(attempted, false))
        this.reportRefusal(error, (fields, message) => {
          this.passwordErrors = fields
          this.passwordMessage = message
        })
      } finally {
        this.changing = false
      }
    },

    adopt (fields: PasswordFields) {
      this.currentPassword = fields.currentPassword
      this.newPassword = fields.newPassword
    },

    // One place that turns a thrown error into what the form shows, because the
    // two screens must not disagree about it: field errors go against their
    // fields, and the whole-form message is for the failure that named no field
    // at all. A field error shown as a form message would tell the user to try
    // again with the same value.
    reportRefusal (error: unknown, show: (fields: FieldErrors, message: string) => void) {
      if (error instanceof ProfileRefused) {
        show(
          error.fields,
          Object.keys(error.fields).length === 0 ? error.message : ''
        )

        return
      }

      // Never reached the server, so nothing refused anything. Given its own words
      // rather than being dressed up as a refusal of the form.
      show({}, 'Something went wrong. Please try again.')
    },

    profileErrorsFor (field: string): string[] {
      return this.errors[field] || []
    },

    passwordErrorsFor (field: string): string[] {
      return this.passwordErrors[field] || []
    },

    stamp (at: string): string {
      return format(new Date(at), 'yyyy-MM-dd hh:mm a')
    }
  }
})
